import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import useTranslation from '@src/hooks/useTranslation';

import useEvent from '@src/hooks/useEvent';
import useMediaQuery from '@src/hooks/useMediaQuery';
import { formatViews } from '@src/utils/course';
import { getShouldShowSoldOut, getTicketStatus } from '@src/utils/event';
import { INFO_STATUS_ON_SALE_COUNTDOWN } from '@src/const/event';
import { selectCountDownForSale } from '@src/store/selectors/event';

import { EyeOutlineIcon } from '@src/components/common/icon';
import ShareButton from '@src/components/common/shareButton';
import EventName from '@src/components/event/basicInfo/eventCard/eventName';

const TimeUnit = styled.span`
	font-size: 14px;
	color: ${props => props.theme.colors.secondary};

	&:not(:first-child) {
		margin-left: 8px;
	}
`;

const TimeValue = styled.span`
	margin-right: 4px;
	font-size: 24px;
	font-weight: ${props => props.fontWeight || 500};
`;

const ViewedCountWrapper = styled.div`
	display: flex;
	align-items: center;
	height: 28px;
	margin-right: 8px;
`;

const DetailsWrapper = styled.div`
	padding: 12px;

	& > *:not(:first-child) {
		margin-top: 8px;
	}
`;

const StyledEventName = styled(EventName)`
	@media ${props => props.theme.device.lgDesktop} {
		display: none;
	}

	@media ${props => props.theme.device.desktop} {
		display: none;
	}
`;

const FlexSpaceBetween = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;

	@media
	${props => props.theme.device.smTablet},
	${props => props.theme.device.mobile} {
		flex-direction: column;
	}
`;

const SocialMedia = styled.div`
	display: flex;
	align-items: center;

	@media
	${props => props.theme.device.smTablet},
	${props => props.theme.device.mobile} {
		margin-top: 8px;
	}
`;

const FlexWrapper = styled.div`
	display: flex;
	align-items: flex-start;

	&:last-child {
		padding-bottom: 16px;
		border-bottom: 1px solid #F0F0F0;

		@media ${props => props.theme.device.lgDesktop} {
			padding-bottom: 0;
			border-bottom: none;
		}

		@media ${props => props.theme.device.desktop} {
			padding-bottom: 0;
			border-bottom: none;
		}
	}
`;

const ChipsWrapper = styled.div`
	margin-top: -4px;

	& > *:not(:last-child) {
		margin-right: 8px;
	}
`;

const Chip = styled.div`
	display: inline-flex;
	padding: 4px 9px 4px 12px;
	border-radius: 50px;
	border: 1px solid #DDD;
	margin-top: 4px;
	color: #333333;
	line-height: 20px;
	font-size: 14px;
`;

const Divider = styled.div`
	width: 1px;
	height: 16px;
	margin: 0px 20px;
	background: #DDDDDD;
`;

const ViewedCount = styled.div`
	display: flex;
	align-items: center;

	& > *:first-child {
		margin-right: 8px;
	}
`;

const CountdownWrapper = styled.div`
	display: flex;
	align-items: center;
`;

const TimerLabel = styled.div`
	margin-right: 16px;
	font-size: 16px;
	color: #101010;
`;

interface IDetails {
	keywords: string[];
	viewedCount: number;
}

const Details = ({ keywords, viewedCount }: IDetails): JSX.Element => {
	const { t } = useTranslation();
	const { event: { slots }, sortedEventSlots: sortedSlots } = useEvent();
	const { status } = getTicketStatus({ slots });
	const countdown = useSelector(selectCountDownForSale);
	const { mediaMatch: { isDesktop, isLgDesktop } } = useMediaQuery();
	const isSoldOut = getShouldShowSoldOut({ sortedSlots });
	const isOnSaleCountdown = status === INFO_STATUS_ON_SALE_COUNTDOWN;
	const timerLabel = status === INFO_STATUS_ON_SALE_COUNTDOWN ?
		t('event:countdown_for_sale') :
		t('event:events_countdown');

	return (
		<DetailsWrapper>
			{!(isDesktop || isLgDesktop) && <StyledEventName />}
			<FlexSpaceBetween>
				<FlexWrapper>
					<ChipsWrapper>
						{keywords.map(keyword => (
							<Chip key={keyword}>{`＃${keyword}`}</Chip>
						))}
					</ChipsWrapper>
					<ViewedCountWrapper>
						<Divider />
						<ViewedCount>
							<EyeOutlineIcon fill="#676767" />
							<span>{formatViews(viewedCount)}</span>
						</ViewedCount>
					</ViewedCountWrapper>
				</FlexWrapper>
				<SocialMedia>
					<ShareButton
						text={t('course:share')}
						modalTitle={t('share_button:title')}
					/>
				</SocialMedia>
			</FlexSpaceBetween>
			{(!isSoldOut && !isOnSaleCountdown) && (
				<FlexWrapper>
					<CountdownWrapper>
						<TimerLabel>{timerLabel}</TimerLabel>
						<div className="timer-content">
							<TimeUnit>
								<TimeValue>{countdown.days}</TimeValue>
								{t('time:day')}
							</TimeUnit>
							<TimeUnit>
								<TimeValue>{countdown.hours}</TimeValue>
								{t('time:hour')}
							</TimeUnit>
							<TimeUnit>
								<TimeValue>{countdown.minutes}</TimeValue>
								{t('time:minute')}
							</TimeUnit>
							<TimeUnit>
								<TimeValue>{countdown.seconds}</TimeValue>
								{t('time:second')}
							</TimeUnit>
						</div>
					</CountdownWrapper>
				</FlexWrapper>
			)}
		</DetailsWrapper>
	);
};

export default Details;
